import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useSEO, generateWebPageSchema, generateBreadcrumbSchema } from '../../utils/seo';

export default function CareersPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useSEO({
    title: '求人情報 | 屋根工事職人募集 | おかやま屋根工事株式会社 | 岡山市',
    description: '岡山市の屋根工事職人を募集中。未経験者歓迎、充実の研修制度で一人前の職人を目指せます。週休2日制、社会保険完備、資格取得支援あり。アットホームな職場で一緒に働きませんか。',
    keywords: '岡山市,屋根工事,求人,職人募集,未経験歓迎,研修制度,資格取得支援',
    schema: {
      '@context': 'https://schema.org',
      '@graph': [
        generateWebPageSchema('求人情報', '岡山市の屋根工事職人を募集中。未経験者歓迎、充実の研修制度で一人前の職人を目指せます。'),
        generateBreadcrumbSchema([
          { name: 'ホーム', url: '/' },
          { name: '求人情報', url: '/careers' }
        ])
      ]
    }
  });

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative h-[60vh] min-h-[500px] flex items-center justify-center">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/43c9e5aa26cfa3d13c96754daeb57620.jpg"
            alt="求人情報"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/40 to-black/50"></div>
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full text-center">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6">一緒に働きませんか</h1>
          <p className="text-lg sm:text-xl text-white/90 max-w-2xl mx-auto">
            未経験者歓迎・充実の研修制度で一人前の職人を目指せます
          </p>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 sm:py-20 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 sm:mb-16">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">働きやすい環境</h2>
            <p className="text-base sm:text-lg text-gray-600">当社で働く魅力をご紹介します</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8">
            <BenefitCard
              icon="ri-user-smile-line"
              title="未経験OK"
              description="経験がなくても大丈夫。先輩職人が丁寧に指導します。基礎から学べる研修制度で、安心してスタートできます。"
            />
            <BenefitCard
              icon="ri-medal-line"
              title="資格取得支援"
              description="各種資格取得を全面サポート。受験費用の補助や勉強時間の確保など、キャリアアップを応援します。"
            />
            <BenefitCard
              icon="ri-line-chart-line"
              title="昇給制度"
              description="頑張りをしっかり評価。年1回の昇給査定で、スキルと経験に応じた給与アップが可能です。"
            />
            <BenefitCard
              icon="ri-calendar-check-line"
              title="休日充実"
              description="週休2日制で、プライベートも大切に。有給休暇も取得しやすい環境です。"
            />
            <BenefitCard
              icon="ri-shield-check-line"
              title="社会保険完備"
              description="健康保険、厚生年金、雇用保険、労災保険を完備。安心して長く働ける環境を整えています。"
            />
            <BenefitCard
              icon="ri-team-line"
              title="アットホームな職場"
              description="少人数の会社だからこそ、チームワークを大切に。風通しの良い職場環境です。"
            />
          </div>
        </div>
      </section>

      {/* Job Details */}
      <section className="py-16 sm:py-20 lg:py-24 bg-gray-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 sm:mb-16">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900">募集要項</h2>
          </div>
          
          <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
            <div className="divide-y divide-gray-200">
              <JobDetailRow label="職種" value="屋根工事職人" />
              <JobDetailRow label="雇用形態" value="正社員" />
              <JobDetailRow label="給与" value="月給 220,000円〜400,000円（経験・能力により優遇）" />
              <JobDetailRow label="勤務時間" value="8:00〜17:00（実働8時間、休憩1時間）" />
              <JobDetailRow label="休日" value="週休2日制（日曜・祝日、その他1日）、夏季休暇、年末年始休暇、有給休暇" />
              <JobDetailRow label="待遇・福利厚生" value="昇給年1回、賞与年2回、社会保険完備、交通費支給、資格取得支援制度、作業着貸与、工具貸与" />
              <JobDetailRow label="応募資格" value="未経験者歓迎、要普通自動車免許（AT限定可）、やる気のある方大歓迎" />
              <JobDetailRow label="勤務地" value="岡山市北区○○町1-2-3（現場により岡山県内各地）" />
              <JobDetailRow label="選考プロセス" value="書類選考 → 面接（1回） → 採用" />
            </div>
          </div>
        </div>
      </section>

      {/* Employee Voices */}
      <section className="py-16 sm:py-20 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 sm:mb-16">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">先輩社員の声</h2>
            <p className="text-base sm:text-lg text-gray-600">実際に働いている社員のリアルな声をお届けします</p>
          </div>
          
          <div className="space-y-8 sm:space-y-12">
            <EmployeeVoice
              name="田中 健一"
              position="職人（入社5年目）"
              age="28歳"
              image="https://readdy.ai/api/search-image?query=Professional%20portrait%20of%20young%20Japanese%20male%20roofing%20worker%20in%20work%20uniform%20smiling%20confidently%2C%20happy%20employee%20without%20any%20text%20or%20signage%2C%20showing%20job%20satisfaction&width=500&height=600&seq=employee001&orientation=portrait"
              interview="未経験から入社しましたが、先輩方が本当に丁寧に教えてくれました。最初は不安でしたが、今では一人で現場を任されるまでに成長できました。お客様から「ありがとう」と言われる瞬間が一番嬉しいです。資格取得も会社がサポートしてくれるので、着実にスキルアップできています。"
            />
            <EmployeeVoice
              name="山本 大輔"
              position="職人（入社3年目）"
              age="32歳"
              image="https://readdy.ai/api/search-image?query=Professional%20portrait%20of%20Japanese%20male%20roofing%20craftsman%20in%20safety%20gear%20smiling%20warmly%2C%20satisfied%20worker%20without%20any%20text%20or%20signage%2C%20showing%20positive%20work%20attitude&width=500&height=600&seq=employee002&orientation=portrait"
              interview="前職は全く違う業界でしたが、手に職をつけたいと思い転職しました。少人数の会社なので、社長や先輩との距離が近く、分からないことはすぐに聞ける環境です。体を動かす仕事なので大変なこともありますが、完成した屋根を見ると達成感があります。休日もしっかり取れるので、プライベートも充実しています。"
            />
          </div>
        </div>
      </section>

      {/* Work Environment */}
      <section className="py-16 sm:py-20 lg:py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 sm:mb-16">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">職場環境</h2>
            <p className="text-base sm:text-lg text-gray-600">働きやすい環境づくりに力を入れています</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8">
            <div className="relative h-80 rounded-xl overflow-hidden shadow-lg">
              <img
                src="https://static.readdy.ai/image/2333ec8ce0f23b8d6e87acea74053a0e/0873815a683349ce19cbf3ad97ed5360.png"
                alt="職場環境1"
                className="w-full h-full object-cover object-top"
              />
            </div>
            <div className="relative h-80 rounded-xl overflow-hidden shadow-lg">
              <img
                src="https://readdy.ai/api/search-image?query=Japanese%20workers%20in%20safety%20training%20session%20learning%20roofing%20techniques%2C%20professional%20training%20environment%20without%20any%20text%20or%20signage%2C%20showing%20education%20and%20skill%20development&width=800&height=600&seq=workplace002&orientation=landscape"
                alt="職場環境2"
                className="w-full h-full object-cover object-top"
              />
            </div>
            <div className="relative h-80 rounded-xl overflow-hidden shadow-lg">
              <img
                src="https://readdy.ai/api/search-image?query=Clean%20and%20organized%20Japanese%20roofing%20company%20workshop%20with%20tools%20and%20equipment%2C%20professional%20work%20environment%20without%20any%20text%20or%20signage%2C%20showing%20workplace%20organization&width=800&height=600&seq=workplace003&orientation=landscape"
                alt="職場環境3"
                className="w-full h-full object-cover object-top"
              />
            </div>
            <div className="relative h-80 rounded-xl overflow-hidden shadow-lg">
              <img
                src="https://static.readdy.ai/image/2333ec8ce0f23b8d6e87acea74053a0e/03f60ccdc85030443706689177c7d58e.jpeg"
                alt="職場環境4"
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Application CTA */}
      <section className="relative py-16 sm:py-20 lg:py-24">
        <div className="absolute inset-0">
          <img
            src="https://static.readdy.ai/image/2333ec8ce0f23b8d6e87acea74053a0e/cfcb1171214ff917e1b93cea0429cb93.jpeg"
            alt="採用応募"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-black/50"></div>
        </div>
        
        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6">
            私たちと一緒に働きませんか？
          </h2>
          <p className="text-lg sm:text-xl text-white/90 mb-8 sm:mb-12 leading-relaxed">
            未経験からでも一人前の職人を目指せます。<br />
            まずはお気軽にお問い合わせください。
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 sm:gap-6 justify-center">
            <a
              href="tel:0860123456"
              className="inline-flex items-center justify-center px-8 py-4 text-base sm:text-lg font-bold text-white bg-[#ff6b35] rounded-lg hover:bg-[#e55a28] transition-all duration-300 shadow-lg whitespace-nowrap cursor-pointer"
            >
              <i className="ri-phone-fill mr-2 text-xl"></i>
              電話で応募する
            </a>
            <Link
              to="/contact"
              className="inline-flex items-center justify-center px-8 py-4 text-base sm:text-lg font-bold text-[#1e3a5f] bg-white rounded-lg hover:bg-gray-100 transition-all duration-300 shadow-lg whitespace-nowrap cursor-pointer"
            >
              <i className="ri-mail-line mr-2 text-xl"></i>
              メールで応募する
            </Link>
          </div>
          
          <p className="text-sm sm:text-base text-white/80 mt-8">
            ご応募の際は、履歴書（写真貼付）をご用意しております。<br />
            ご不明な点がございましたら、お気軽にお問い合わせください。
          </p>
        </div>
      </section>
    </div>
  );
}

function BenefitCard({ icon, title, description }: { icon: string; title: string; description: string }) {
  return (
    <div className="bg-white rounded-xl p-6 sm:p-8 shadow-lg hover:shadow-xl transition-shadow duration-300">
      <div className="w-16 h-16 bg-[#1e3a5f]/10 rounded-full flex items-center justify-center mb-6">
        <i className={`${icon} text-3xl text-[#1e3a5f]`}></i>
      </div>
      <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4">{title}</h3>
      <p className="text-gray-600 leading-relaxed text-sm sm:text-base">{description}</p>
    </div>
  );
}

function JobDetailRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 px-6 sm:px-8 py-5 sm:py-6">
      <div className="text-gray-500 font-semibold text-sm sm:text-base">{label}</div>
      <div className="sm:col-span-2 text-gray-900 font-medium text-sm sm:text-base whitespace-pre-line">{value}</div>
    </div>
  );
}

function EmployeeVoice({ name, position, age, image, interview }: { name: string; position: string; age: string; image: string; interview: string }) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
      <div className="order-2 lg:order-1">
        <div className="mb-6">
          <h3 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">{name}</h3>
          <p className="text-[#1e3a5f] font-semibold mb-1 text-base sm:text-lg">{position}</p>
          <p className="text-gray-600 text-sm sm:text-base">{age}</p>
        </div>
        <div className="bg-gray-50 rounded-xl p-6 sm:p-8">
          <i className="ri-double-quotes-l text-4xl text-[#1e3a5f]/20 mb-4 block"></i>
          <p className="text-gray-700 leading-relaxed text-sm sm:text-base">{interview}</p>
        </div>
      </div>
      <div className="order-1 lg:order-2">
        <div className="w-full h-[400px] sm:h-[500px]">
          <img
            src={image}
            alt={name}
            className="w-full h-full object-cover object-top rounded-2xl shadow-lg"
          />
        </div>
      </div>
    </div>
  );
}
